import { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import FormField from './FormField';
import { isCleanText, CLEAN_TEXT_MESSAGE } from '@/utils/sanitize';

export interface TagsFieldProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  value?: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  className?: string;
  /** VAPT #12/#13 - set true for tags that legitimately need symbols. */
  allowSpecialChars?: boolean;
}

/**
 * Reusable tags input field component
 * Allows adding/removing tags with keyboard support (Enter to add)
 */
export default function TagsField({
  label,
  description,
  error,
  required = false,
  value = [],
  onChange,
  placeholder = 'Add a tag and press Enter',
  maxTags,
  className = '',
  allowSpecialChars = false,
}: TagsFieldProps) {
  const [tags, setTags] = useState<string[]>(value);
  const [inputValue, setInputValue] = useState('');
  // Error for a rejected tag; the typed text stays so it can be corrected.
  const [inputError, setInputError] = useState('');
  const shownError = error || inputError;

  useEffect(() => {
    setTags(value || []);
  }, [value]);

  // Shared by the Enter key and the Add button.
  const addTag = () => {
    const newTag = inputValue.trim();
    if (!newTag) return;

    if (!allowSpecialChars && !isCleanText(newTag)) {
      setInputError(CLEAN_TEXT_MESSAGE);
      return;
    }

    // Check if tag already exists
    if (tags.includes(newTag)) {
      setInputValue('');
      return;
    }

    // Check max tags limit
    if (maxTags && tags.length >= maxTags) {
      setInputValue('');
      return;
    }

    const newTags = [...tags, newTag];
    setTags(newTags);
    onChange(newTags);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // While an input method (IME) is composing, Enter only confirms the word, so don't add yet.
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      const newTags = tags.slice(0, -1);
      setTags(newTags);
      onChange(newTags);
    }
  };

  const removeTag = (tagToRemove: string) => {
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(newTags);
    onChange(newTags);
  };

  const canAddMore = !maxTags || tags.length < maxTags;

  return (
    <FormField
      label={label}
      description={description}
      error={shownError}
      required={required}
      className={className}
    >
      <div
        className={`flex flex-wrap items-center gap-2 p-3 border rounded-lg transition-all ${
          shownError
            ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
            : 'border-gray-300 focus-within:ring-2 focus-within:ring-primary-500'
        }`}
      >
        {/* Tags */}
        {tags.map((tag, index) => (
          <div
            key={index}
            className="inline-flex items-center gap-1.5 max-w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium px-3 py-1.5 rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <span className="min-w-0 break-all">{tag}</span>
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="ml-1 flex-shrink-0 hover:bg-white/20 rounded-full p-0.5 transition-colors"
              aria-label={`Remove ${tag}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {/* Input */}
        {canAddMore && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (inputError) setInputError('');
            }}
            onKeyDown={handleKeyDown}
            className="flex-grow min-w-[120px] bg-transparent focus:outline-none text-sm placeholder:text-gray-400"
            placeholder={tags.length === 0 ? placeholder : 'Add more...'}
          />
        )}

        {/* Add button - works without the Enter key (IME keyboards, touch devices) */}
        {canAddMore && (
          <button
            type="button"
            onClick={addTag}
            disabled={!inputValue.trim()}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        )}

        {/* Max tags indicator */}
        {maxTags && tags.length >= maxTags && (
          <span className="text-xs text-gray-500 italic">Max {maxTags} tags</span>
        )}
      </div>

      {/* Helper text */}
      {!shownError && (
        <p className="text-xs text-gray-500 mt-1.5">
          <Plus className="w-3 h-3 inline mr-1" />
          Press Enter or click Add to add a tag
          {maxTags && ` (${tags.length}/${maxTags})`}
        </p>
      )}
    </FormField>
  );
}
